import { uniq, without } from "lodash";
import create from "zustand";
import { persist } from "zustand/middleware";
import { Entity } from "../../../utils/constants";

export interface SortOrder {
  iteratee: string;
  order: "desc" | "asc";
}

export interface Property<T> {
  key: string;
  title: string | React.ReactNode;
  style?: React.CSSProperties;
  sortIteratee?: string;
  onHeaderClick?: () => void;
  excludeForExport?: boolean;
  renderForExport?: (item: T) => any;
  renderContent: (item: T) => string | React.ReactNode | null;
}

export type DataTablesState = {
  searchInput: { [entity in keyof typeof Entity]: string };
  updateSearchInput: (entity: Entity, newInputValue: string) => void;
  visibleProperties: { [entity in keyof typeof Entity]: string[] };
  updateVisibleProperties: (entity: Entity, propertyKeys: string[]) => void;
  orderedProperties: { [entity in keyof typeof Entity]: string[] };
  updateOrderedProperties: (entity: Entity, propertyKeys: string[]) => void;
  sortOrder: { [entity in keyof typeof Entity]: SortOrder | undefined };
  updateSortOrder: (
    entity: Entity,
    newSortOrder: SortOrder | undefined
  ) => void;
};

const useDataTable = create<DataTablesState>(
  persist(
    (set, get) => ({
      searchInput: {
        [Entity.hotspot]: "",
        [Entity.location]: "",
        [Entity.hex]: "",
        [Entity.wallet]: "",
        [Entity.contact]: "",
        [Entity.install]: "",
        [Entity.label]: "",
        [Entity.commissionTemplate]: "",
        [Entity.commissionReport]: "",
        [Entity.contactCommissions]: "",
        [Entity.transaction]: "",
        [Entity.contactPayment]: "",
        [Entity.hotspotMaker]: ""
      },
      updateSearchInput: (entity, newInputValue) =>
        set((state) => ({
          searchInput: {
            ...state.searchInput,
            [entity]: newInputValue
          }
        })),
      visibleProperties: {
        [Entity.hotspot]: [
          "name",
          "addInstall",
          "labels",
          "messages",
          "day",
          "dayWeekAverage",
          "dayPercentageChangeToDayWeekAverage",
          "syncingSince",
          "lastAsserted",
          "activated",
          "simulatedTransmitScale",
          "transmitScale",
          "witnessRewardUnits",
          "actions"
        ],
        [Entity.location]: [
          "name",
          "newSuperSimulation",
          "lastSuperSimulation",
          "addInstall",
          "labels",
          "address",
          "coordinates",
          "height",
          "linkedContactsCount",
          "activated",
          "simulatedTransmitScale",
          "created_at",
          "actions"
        ],
        [Entity.hex]: [],
        [Entity.wallet]: [
          "address",
          "labels",
          "balance_data.balance",
          "balance_data.mobile_balance",
          "hotspot_count",
          "lifetime",
          "tracked",
          "verified",
          "current_verification_status",
          "actions"
        ],
        [Entity.contact]: [
          "name",
          "labels",
          "email",
          "phone",
          "payment_methods",
          "linkedLocationsCount",
          "hosting",
          "created_at",
          "updated_at",
          "actions"
        ],
        [Entity.install]: [
          "installs",
          "labels",
          "installed_at",
          "uninstalled_at",
          "created_at",
          "updated_at",
          "actions"
        ],
        [Entity.label]: ["name", "type", "usage", "actions"],
        [Entity.commissionTemplate]: ["template", "agreement", "actions"],
        [Entity.commissionReport]: [
          "open",
          "title",
          "currency",
          "period",
          "total_commission_amount",
          "created_at",
          "actions"
        ],
        [Entity.contactCommissions]: [
          "contact",
          "defaultPaymentMethod",
          "status",
          "totalCommissionValue",
          "commissions",
          "open"
        ],
        [Entity.transaction]: [
          "date",
          "amount",
          "sender",
          "recipients",
          "memo",
          "status",
          "open",
          "explorer"
        ],
        [Entity.contactPayment]: [
          "created_at",
          "paid_at",
          "amount",
          "currency",
          "context",
          "notes",
          "open",
          "actions"
        ],
        [Entity.hotspotMaker]: []
      },
      updateVisibleProperties: (entity, propertyKeys) =>
        set((state) => ({
          visibleProperties: {
            ...state.visibleProperties,
            [entity]: propertyKeys
          }
        })),
      orderedProperties: {
        [Entity.hotspot]: [
          "wallet",
          "place",
          "id",
          "name",
          "historicalValues",
          "addInstall",
          "labels",
          "messages",
          "day",
          "dayWeekAverage",
          "dayPercentageChangeToDayWeekAverage",
          "week",
          "previousWeek",
          "weekPercentageChange",
          "month",
          "previousMonth",
          "monthPercentageChange",
          "currentCalendarMonth",
          "previousCalendarMonth",
          "lifetime",
          "allWitnessedBy",
          "validWitnessedBy",
          "invalidWitnessedBy",
          "allWitnessedOthers",
          "validWitnessedOthers",
          "invalidWitnessedOthers",
          "allBidirectional",
          "validBidirectional",
          "invalidBidirectional",
          "activated",
          "simulatedTransmitScale",
          "transmitScale",
          "witnessRewardUnits",
          "placement",
          "timestampAdded",
          "online",
          "lastUpdate",
          "lastAsserted",
          "elevation",
          "gain",
          "maker",
          "actions"
        ],
        [Entity.location]: [
          "name",
          "newSuperSimulation",
          "lastSuperSimulation",
          "addInstall",
          "labels",
          "address",
          "coordinates",
          "height",
          "linkedContactsCount",
          "activated",
          "simulatedTransmitScale",
          "created_at",
          "updated_at",
          "actions"
        ],
        [Entity.hex]: [],
        [Entity.wallet]: [
          "address",
          "labels",
          "balance_data.balance",
          "balance_data.mobile_balance",
          "balance_data.iot_balance",
          "balance_data.dc_balance",
          "balance_data.staked_balance",
          "hotspot_count",
          "lifetime",
          "tracked",
          "verified",
          "current_verification_status",
          "created_at",
          "updated_at",
          "actions"
        ],
        [Entity.contact]: [
          "name",
          "labels",
          "email",
          "phone",
          "vat",
          "payment_methods",
          "linkedLocationsCount",
          "hosting",
          "created_at",
          "updated_at",
          "actions"
        ],
        [Entity.install]: [
          "installs",
          "hotspot",
          "location",
          "labels",
          "installed_at",
          "uninstalled_at",
          "created_at",
          "updated_at",
          "actions"
        ],
        [Entity.label]: ["name", "type", "usage", "actions"],
        [Entity.commissionTemplate]: ["template", "agreement", "actions"],
        [Entity.commissionReport]: [
          "open",
          "title",
          "currency",
          "period",
          "total_commission_amount",
          "created_at",
          "start_date",
          "end_date",
          "timezone",
          "actions"
        ],
        [Entity.contactCommissions]: [
          "contact",
          "defaultPaymentMethod",
          "status",
          "totalCommissionValue",
          "commissions",
          "open"
        ],
        [Entity.transaction]: [
          "date",
          "amount",
          "sender",
          "recipients",
          "memo",
          "status",
          "open",
          "explorer",
          "blockHeight"
        ],
        [Entity.contactPayment]: [
          "created_at",
          "paid_at",
          "amount",
          "currency",
          "context",
          "notes",
          "open",
          "actions"
        ],
        [Entity.hotspotMaker]: []
      },
      updateOrderedProperties: (entity, propertyKeys) =>
        set((state) => ({
          orderedProperties: {
            ...state.orderedProperties,
            [entity]: propertyKeys
          }
        })),
      sortOrder: {
        [Entity.hotspot]: { iteratee: "name", order: "asc" },
        [Entity.location]: { iteratee: "name", order: "asc" },
        [Entity.hex]: undefined,
        [Entity.wallet]: { iteratee: "balance_data.balance", order: "desc" },
        [Entity.contact]: { iteratee: "name", order: "asc" },
        [Entity.install]: { iteratee: "created_at", order: "desc" },
        [Entity.label]: { iteratee: "name", order: "asc" },
        [Entity.commissionTemplate]: {
          iteratee: "name",
          order: "asc"
        },
        [Entity.commissionReport]: {
          iteratee: "created_at",
          order: "desc"
        },
        [Entity.contactCommissions]: {
          iteratee: "commissions",
          order: "desc"
        },
        [Entity.transaction]: { iteratee: "time", order: "desc" },
        [Entity.contactPayment]: undefined,
        [Entity.hotspotMaker]: { iteratee: "name", order: "desc" }
      },
      updateSortOrder: (entity, newSortOrder) =>
        set((state) => ({
          sortOrder: {
            ...state.sortOrder,
            [entity]: newSortOrder
          }
        }))
    }),
    {
      name: "data_tables",
      version: 79,
      migrate: (persistedState, version) => {
        if (version < 68) {
          persistedState.orderedProperties[Entity.contactCommissions] = without(
            persistedState.orderedProperties[Entity.contactCommissions],
            "totalCommissionValueUsd"
          );
          persistedState.visibleProperties[Entity.contactCommissions] = without(
            persistedState.visibleProperties[Entity.contactCommissions],
            "totalCommissionValueUsd"
          );
        }

        if (version < 69) {
          persistedState.orderedProperties[Entity.hotspot] = [
            "id",
            ...persistedState.orderedProperties[Entity.hotspot]
          ];
        }

        if (version < 71) {
          const contactPaymentProperties = [
            "created_at",
            "paid_at",
            "amount",
            "currency",
            "context",
            "notes",
            "open",
            "actions"
          ];
          persistedState.searchInput[Entity.contactPayment] = "";
          persistedState.visibleProperties[Entity.contactPayment] =
            contactPaymentProperties;
          persistedState.orderedProperties[Entity.contactPayment] =
            contactPaymentProperties;
          persistedState.sortOrder[Entity.contactPayment] = undefined;
        }

        if (version < 72) {
          persistedState.visibleProperties[Entity.commissionReport] = [
            "open",
            "title",
            "currency",
            "period",
            "total_commission_amount",
            "created_at",
            "actions"
          ];
          persistedState.orderedProperties[Entity.commissionReport] = [
            "open",
            "title",
            "currency",
            "period",
            "total_commission_amount",
            "created_at",
            "start_date",
            "end_date",
            "timezone",
            "actions"
          ];
        }

        if (version < 73) {
          persistedState.orderedProperties[Entity.install] = without(
            persistedState.orderedProperties[Entity.install],
            "created_by"
          );
          persistedState.visibleProperties[Entity.install] = without(
            persistedState.visibleProperties[Entity.install],
            "created_by"
          );
          persistedState.orderedProperties[Entity.location] = without(
            persistedState.orderedProperties[Entity.location],
            "created_by"
          );
          persistedState.visibleProperties[Entity.location] = without(
            persistedState.visibleProperties[Entity.location],
            "created_by"
          );
        }

        if (version < 74) {
          const locationProperties = [
            "name",
            "newSuperSimulation",
            "lastSuperSimulation",
            "addInstall",
            "labels",
            "address",
            "coordinates",
            "height",
            "linkedContactsCount",
            "activated",
            "simulatedTransmitScale",
            "created_at",
            "actions"
          ];
          persistedState.visibleProperties[Entity.location] =
            locationProperties;
          persistedState.orderedProperties[Entity.location] =
            locationProperties;
        }

        if (version < 75) {
          persistedState.orderedProperties[Entity.hotspot] = [
            "wallet",
            "place",
            ...persistedState.orderedProperties[Entity.hotspot]
          ];
        }

        if (version < 76) {
          persistedState.orderedProperties[Entity.hotspot] = uniq(
            persistedState.orderedProperties[Entity.hotspot]
          );
          persistedState.orderedProperties[Entity.hotspot] = without(
            persistedState.orderedProperties[Entity.hotspot],
            "relayed",
            "syncingSince",
            "blocksToSync"
          );
          persistedState.visibleProperties[Entity.hotspot] = without(
            persistedState.visibleProperties[Entity.hotspot],
            "relayed",
            "syncingSince",
            "blocksToSync"
          );
        }

        if (version < 77) {
          persistedState.visibleProperties[Entity.wallet] = [
            "address",
            "labels",
            "hnt_balance",
            "mobile_balance",
            "hotspot_count",
            "lifetime",
            "tracked",
            "verified",
            "current_verification_status",
            "actions"
          ];
          persistedState.orderedProperties[Entity.wallet] = [
            "address",
            "labels",
            "hnt_balance",
            "mobile_balance",
            "iot_balance",
            "dc_balance",
            "staked_balance",
            "hotspot_count",
            "lifetime",
            "tracked",
            "verified",
            "current_verification_status",
            "created_at",
            "updated_at",
            "actions"
          ];
        }

        if (version < 78) {
          persistedState.visibleProperties[Entity.wallet] = [
            "address",
            "labels",
            "balance",
            "mobile_balance",
            "hotspot_count",
            "lifetime",
            "tracked",
            "verified",
            "current_verification_status",
            "actions"
          ];
          persistedState.orderedProperties[Entity.wallet] = [
            "address",
            "labels",
            "balance_data.balance",
            "balance_data.mobile_balance",
            "balance_data.iot_balance",
            "balance_data.dc_balance",
            "balance_data.staked_balance",
            "hotspot_count",
            "lifetime",
            "tracked",
            "verified",
            "current_verification_status",
            "created_at",
            "updated_at",
            "actions"
          ];
          persistedState.sortOrder[Entity.wallet] = [
            { iteratee: "balance_data.balance", order: "desc" }
          ];
        }

        if (version < 79) {
          persistedState.visibleProperties[Entity.wallet] = [
            "address",
            "labels",
            "hotspot_count",
            "lifetime",
            "tracked",
            "verified",
            "current_verification_status",
            "actions",
            "balance_data.balance",
            "balance_data.mobile_balance"
          ];
        }

        return persistedState;
      }
    }
  )
);

export default useDataTable;
